import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { useNavigate, Link } from 'react-router-dom';
import GroupIcon from '@mui/icons-material/Group';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import CodeIcon from '@mui/icons-material/Code';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { motion } from 'framer-motion';

const services = [
  {
    title: 'Staff Augmentation',
    description: 'Instantly scale your team with top tech talent.',
    icon: <GroupIcon sx={{ fontSize: 40, color: '#888888', mb: 2 }} />,
    url: 'staff-augmentation',
  },
  {
    title: 'Product Studio',
    description: 'From idea to launch, we build your product.',
    icon: <DesignServicesIcon sx={{ fontSize: 40, color: '#888888', mb: 2 }} />,
    url: 'product-studio',
  },
  {
    title: 'Project Sprints',
    description: 'Agile sprints for rapid, high-quality delivery.',
    icon: <FlashOnIcon sx={{ fontSize: 40, color: '#888888', mb: 2 }} />,
    url: 'project-sprints',
  },
  {
    title: 'IT Outsourcing',
    description: 'Expert IT teams & consulting for US businesses.',
    icon: <BusinessCenterIcon sx={{ fontSize: 40, color: '#888888', mb: 2 }} />,
    url: 'it-outsourcing',
  },
  {
    title: 'Custom Software Dev',
    description: 'Bespoke software built for your exact needs.',
    icon: <CodeIcon sx={{ fontSize: 40, color: '#888888', mb: 2 }} />,
    url: 'custom-software-development',
  },
  {
    title: 'GoHighLevel Expert',
    description: 'GHL automation, funnels & white-label SaaS.',
    icon: <AutoFixHighIcon sx={{ fontSize: 40, color: '#888888', mb: 2 }} />,
    url: 'gohighlevel-expert',
  },
];

const borderColors = [
  '#6366F1', // Indigo
  '#EC4899', // Pink
  '#F59E42', // Orange
  '#10B981', // Emerald
  '#8B5CF6', // Violet
  '#06B6D4', // Cyan
];

const serviceHighlights = [
  'Dedicated Teams',
  'Rapid Onboarding',
  'Transparent Communication',
  'Scalable Solutions',
];

interface ServicesMegaMenuProps {
  onClose: () => void;
}

const ServicesMegaMenu: React.FC<ServicesMegaMenuProps> = ({ onClose }) => {
  const navigate = useNavigate();
  // Track fill origin (x, y) for each card (default to center)
  const [fillOrigins, setFillOrigins] = useState<{ x: string; y: string }[]>(Array(services.length).fill({ x: '50%', y: '50%' }));

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    const card = e.currentTarget;
    const clientX = e.clientX;
    const clientY = e.clientY;
    const i = index;
    requestAnimationFrame(() => {
      const rect = card.getBoundingClientRect();
      const x = clientX - rect.left;
      const y = clientY - rect.top;
      const percentX = `${(x / rect.width) * 100}%`;
      const percentY = `${(y / rect.height) * 100}%`;
      setFillOrigins((prev) => {
        const next = [...prev];
        next[i] = { x: percentX, y: percentY };
        return next;
      });
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'absolute',
        top: '100%',
        left: '20%',
        transform: 'translateX(-60%)',
        width: '90vw',
        maxWidth: '1400px',
        mt: 2,
        py: 6,
        px: { xs: 2, sm: 4, md: 6 },
        bgcolor: '#ffffff',
        borderRadius: 2,
        border: '2px solid rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
        zIndex: 1300,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '2px',
          borderRadius: 'calc(8px - 2px)',
          border: '2px solid rgba(0, 0, 0, 0.06)',
          pointerEvents: 'none'
        }
      }}
    >
      <Box sx={{ width: '100%', margin: '0 auto', display: 'flex' }}>
        <Box sx={{ flex: '0 0 75%', pr: 4 }}>
          <Grid container spacing={4} justifyContent="center">
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={service.title}>
                <Link
                  to={`/services/${service.url}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                  onClick={onClose}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Box
                      onMouseEnter={e => handleMouseEnter(e, index)}
                      sx={{
                        p: 2,
                        height: '100%',
                        minHeight: 140,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        transition: 'all 0.3s ease',
                        borderRadius: 2,
                        backgroundColor: '#fff',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        zIndex: 0,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          border: `2px solid ${borderColors[index]}`,
                          borderRadius: 'inherit',
                          transition: 'all 0.3s ease',
                          pointerEvents: 'none',
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: `calc(${fillOrigins[index]?.x} - 50vw)`,
                          top: `calc(${fillOrigins[index]?.y} - 50vw)`,
                          width: '100vw',
                          height: '100vw',
                          background: `${borderColors[index]}22`, // 22 = ~13% opacity
                          borderRadius: '50%',
                          zIndex: 0,
                          pointerEvents: 'none',
                          transform: 'scale(0)',
                          transition: 'transform 0.3s cubic-bezier(0.42,0,1,1)', // faster unfill animation
                        },
                        '&:hover': {
                          backgroundColor: '#f9fafb',
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px -8px rgba(0, 0, 0, 0.12)',
                          '&::before': {
                            borderColor: borderColors[index],
                            borderWidth: '2px',
                          },
                          '&::after': {
                            transform: 'scale(1)',
                            transition: 'transform 0.7s cubic-bezier(0,0,0.58,1)', // classic ease-out (fast end)
                          },
                        },
                      }}
                    >
                      <Box sx={{ mb: 2, position: 'relative', zIndex: 1 }}>{service.icon}</Box>
                      <Typography variant="h6" sx={{ color: '#18191c', fontWeight: 600, mb: 2, textAlign: 'center', fontSize: '1.1rem', position: 'relative', zIndex: 1 }}>
                        {service.title}
                      </Typography>
                      <Typography sx={{ color: '#555', fontSize: '0.9rem', mb: 2, textAlign: 'center', lineHeight: 1.4, position: 'relative', zIndex: 1 }}>
                        {service.description}
                      </Typography>
                    </Box>
                  </motion.div>
                </Link>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box
          sx={{
            width: '2px',
            mx: 4,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '10%',
              bottom: '10%',
              width: '2px',
              background: 'rgba(0, 0, 0, 0.12)',
              borderRadius: '2px'
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '2px',
              top: '10%',
              bottom: '10%',
              width: '2px',
              background: 'rgba(0, 0, 0, 0.06)',
              borderRadius: '2px'
            }
          }}
        />
        <Box sx={{ flex: '0 0 25%' }}>
          <Box
            sx={{
              position: 'relative',
              minHeight: '80px',
              p: 1.5,
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='300' height='400' viewBox='0 0 300 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cg stroke='rgba(0, 0, 0, 0.08)' fill='none'%3E%3Cpath d='M150,50 L150,350' stroke-width='1.5' stroke-dasharray='4,4'/%3E%3Cpath d='M100,100 L200,100' stroke-width='1.5' stroke-dasharray='4,4'/%3E%3Cpath d='M100,200 L200,200' stroke-width='1.5' stroke-dasharray='4,4'/%3E%3Cpath d='M100,300 L200,300' stroke-width='1.5' stroke-dasharray='4,4'/%3E%3Cpath d='M120,75 L180,125' stroke-width='1.5'/%3E%3Cpath d='M120,175 L180,225' stroke-width='1.5'/%3E%3Cpath d='M120,275 L180,325' stroke-width='1.5'/%3E%3Cpath d='M180,75 L120,125' stroke-width='1.5'/%3E%3Cpath d='M180,175 L120,225' stroke-width='1.5'/%3E%3Cpath d='M180,275 L120,325' stroke-width='1.5'/%3E%3Ccircle cx='120' cy='75' r='3' stroke-width='2'/%3E%3Ccircle cx='180' cy='125' r='3' stroke-width='2'/%3E%3Ccircle cx='120' cy='175' r='3' stroke-width='2'/%3E%3Ccircle cx='180' cy='225' r='3' stroke-width='2'/%3E%3Ccircle cx='120' cy='275' r='3' stroke-width='2'/%3E%3Ccircle cx='180' cy='325' r='3' stroke-width='2'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-24px',
                left: 0,
                width: '48px',
                height: '3px',
                background: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '2px'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-24px',
                left: '2px',
                width: '48px',
                height: '3px',
                background: 'rgba(0, 0, 0, 0.06)',
                borderRadius: '2px'
              }
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.87)',
                mb: 2,
                position: 'relative',
                zIndex: 1
              }}
            >
              Why Choose Us
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, position: 'relative', zIndex: 1 }}>
              {serviceHighlights.map((highlight, index) => (
                <Box
                  key={highlight}
                  component="li"
                  sx={{
                    mb: 1,
                    fontSize: '0.95rem',
                    color: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    '&::before': {
                      content: '""',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      mr: 2
                    }
                  }}
                >
                  {highlight}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default ServicesMegaMenu; 